import { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useMeta, pkr, waLink } from "@/lib/site";
import { MaskedLines, Overline, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { FragranceCard } from "@/components/FragranceCard";

export default function Home() {
  useMeta(null, "Bonheur Eternal B.E. — a luxury fragrance house from Pakistan. Nine eaux de parfum crafted to be remembered. Delivered all over Pakistan.");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const { data: hero } = useQuery({ queryKey: ["hero"], queryFn: async () => (await api.get("/hero")).data });
  const { data: fragrances = [] } = useQuery({ queryKey: ["fragrances"], queryFn: async () => (await api.get("/fragrances")).data });
  const { data: journal = [] } = useQuery({ queryKey: ["journal"], queryFn: async () => (await api.get("/journal")).data });
  const featured = fragrances.filter((f) => (hero?.featured_slugs || ["pasha", "diva", "nuit-doree"]).includes(f.slug)).slice(0, 3);

  return (
    <div>
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: imgY }}>
          {hero?.image && <img src={hero.image} alt="" className="w-full h-[115%] object-cover object-center opacity-80" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#CFC2AC] via-[#CFC2AC]/30 to-[#CFC2AC]/50" />
        </motion.div>
        <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-24 md:pb-32 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 1 }}
            className="text-[11px] tracking-[0.4em] uppercase text-[#8F6B3A] mb-6"
          >
            {hero?.overline || "Maison de Parfum — Pakistan"}
          </motion.p>
          <MaskedLines
            lines={hero?.headline_lines || ["Fragrance is memory,", "bottled with intention."]}
            lineClassName="font-display text-4xl sm:text-5xl lg:text-7xl text-[#2B2118] tracking-tight leading-[1.08]"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1 }}
            className="mt-8 text-base text-[#5A4C3C] max-w-md leading-relaxed"
          >
            {hero?.subline || "Nine eaux de parfum, composed to linger long after you leave the room."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1 }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <Link
              to="/collection"
              data-testid="hero-cta-collection"
              className="rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300"
            >
              Explore the Collection
            </Link>
            <Link to="/story" data-testid="hero-cta-story" className="text-xs tracking-[0.2em] uppercase text-[#2B2118] border-b border-[#8F6B3A]/50 pb-1 hover:text-[#8F6B3A] transition-colors duration-300">
              The House
            </Link>
            <Link to="/scent-finder" data-testid="hero-cta-scent-finder" className="text-xs tracking-[0.2em] uppercase text-[#5A4C3C] border-b border-[#2B2118]/20 pb-1 hover:text-[#8F6B3A] transition-colors duration-300">
              Find your scent
            </Link>
          </motion.div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1.4 }}
          className="absolute bottom-8 right-8 hidden md:block text-[10px] tracking-[0.4em] uppercase text-[#6E6151] rotate-90 origin-bottom-right"
        >
          Be Remembered.
        </motion.div>
      </section>

      <section className="py-24 md:py-40 px-5 md:px-8 max-w-5xl mx-auto text-center">
        <Reveal>
          <p className="font-display text-2xl md:text-4xl leading-relaxed text-[#2B2118]/90 italic">
            “A fragrance house is not built in a season. It is built each time someone turns around and asks — <span className="text-[#8F6B3A] not-italic">what are you wearing?</span>”
          </p>
        </Reveal>
      </section>

      <section className="py-24 md:py-32 px-5 md:px-8 max-w-7xl mx-auto">
        <Reveal className="flex items-end justify-between mb-16">
          <div>
            <Overline>Featured</Overline>
            <h2 className="font-display text-3xl md:text-4xl text-[#2B2118] mt-3 tracking-tight">From the collection</h2>
          </div>
          <Link to="/collection" data-testid="featured-view-all" className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#5A4C3C] hover:text-[#8F6B3A] transition-colors">
            View all nine <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {featured.map((f, i) => (
            <FragranceCard key={f.slug} f={f} index={i} />
          ))}
        </div>
      </section>

      <Marquee />

      <section className="py-24 md:py-40 px-5 md:px-8 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <Overline>The Discovery Set</Overline>
          <h2 className="font-display text-3xl md:text-5xl text-[#2B2118] mt-4 tracking-tight leading-tight">
            Three women's fragrances.<br />One small ritual.
          </h2>
          <p className="mt-6 text-[#5A4C3C] leading-loose max-w-md">
            Breeze, Diva and Elysian in travel-ready decants — the most considered way to find the scent that will become yours.
          </p>
          <Link to="/decants" data-testid="home-decants-cta" className="inline-block mt-8 text-xs tracking-[0.2em] uppercase text-[#8F6B3A] border-b border-[#8F6B3A]/50 pb-1 hover:text-[#2B2118] transition-colors">
            Discover the set
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-1 gap-4">
            {fragrances.filter((f) => ["breeze", "diva", "elysian"].includes(f.slug)).map((f) => (
              <Link key={f.slug} to={`/fragrance/${f.slug}`} className="block overflow-hidden border border-[#2B2118]/[0.07] group">
                <img src={f.image} alt={f.name} loading="lazy" className="w-full aspect-[21/9] object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="py-24 md:py-32 border-t border-[#2B2118]/[0.06]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <Reveal>
            <Overline>New — Solid Perfume</Overline>
            <h2 className="font-display text-3xl md:text-4xl text-[#2B2118] mt-3 tracking-tight">Your signature,<br />in a 16g travel tin.</h2>
            <p className="mt-5 text-[#5A4C3C] leading-loose max-w-md">All nine fragrances as long-lasting solid perfume — {`PKR 1,099`} each, no spray, no spill.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/solid-perfume" data-testid="home-solid-cta" className="inline-block rounded-full bg-[#8F6B3A] text-[#FBF8F1] px-8 py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#6F5433] transition-colors duration-300">
              Explore Solid Perfume
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-[#2B2118]/[0.06]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-12">
          <Reveal>
            <Overline>The House</Overline>
            <h2 className="font-display text-3xl md:text-4xl text-[#2B2118] mt-3 tracking-tight">Sourced. Crafted.<br />Eternal.</h2>
            <Link to="/story" data-testid="home-story-cta" className="inline-block mt-6 text-xs tracking-[0.2em] uppercase text-[#8F6B3A] border-b border-[#8F6B3A]/50 pb-1 hover:text-[#2B2118] transition-colors">
              Read our story
            </Link>
          </Reveal>
          {journal.slice(0, 2).map((p, i) => (
            <Reveal key={p.slug} delay={0.1 + i * 0.1}>
              <Link to={`/journal/${p.slug}`} data-testid={`home-journal-${p.slug}`} className="group block">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E6151]">Journal — {p.date}</p>
                <h3 className="font-display text-xl text-[#2B2118] mt-3 leading-snug group-hover:text-[#8F6B3A] transition-colors">{p.title}</h3>
                <p className="mt-3 text-sm text-[#5A4C3C] leading-relaxed line-clamp-3">{p.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32 text-center px-5">
        <Reveal>
          <p className="font-display text-3xl md:text-5xl text-[#2B2118] tracking-tight">Be Remembered.</p>
          <a
            href={waLink("Hello Bonheur Eternal, I'd like to place an order.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-whatsapp-cta"
            className="inline-block mt-8 rounded-full border border-[#8F6B3A]/50 text-[#8F6B3A] px-8 py-3.5 text-xs tracking-[0.2em] uppercase hover:bg-[#8F6B3A] hover:text-[#FBF8F1] transition-colors duration-300"
          >
            Order via WhatsApp
          </a>
        </Reveal>
      </section>
    </div>
  );
}
